import { useTranslation } from 'react-i18next';

import { useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import type { Member } from '@nucleus-commerce/api-client/admin';
import { isApiError } from '@nucleus-commerce/api-client/transport';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@nucleus-commerce/ui/components/alert-dialog';

import { useRemoveMember } from '../hooks/use-member-mutations';
import { memberChangeErrorKey } from '../schemas/member-rules';

export interface RemoveMemberDialogProps {
  member: Member | null;
  onOpenChange: (open: boolean) => void;
}

/** Confirms removal; the backend ends the person's sessions at once. */
export function RemoveMemberDialog({ member, onOpenChange }: RemoveMemberDialogProps) {
  const { t } = useTranslation(['team', 'common', 'errors']);
  const remove = useRemoveMember();
  const message = useErrorMessage();
  const errorKey = remove.error ? memberChangeErrorKey(remove.error) : null;
  const requestId = isApiError(remove.error) ? remove.error.requestId : undefined;

  return (
    <AlertDialog
      open={member !== null}
      onOpenChange={(next) => {
        if (!next) remove.reset();
        onOpenChange(next);
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {t('team:remove.title', { name: member?.user.name ?? '' })}
          </AlertDialogTitle>
          <AlertDialogDescription>{t('team:remove.description')}</AlertDialogDescription>
        </AlertDialogHeader>
        {remove.isError ? (
          <Alert variant="destructive" role="alert">
            <AlertDescription>
              {errorKey ? t(errorKey) : message(remove.error)}
              {requestId ? (
                <span className="block text-xs">{t('errors:requestIdShort', { requestId })}</span>
              ) : null}
            </AlertDescription>
          </Alert>
        ) : null}
        <AlertDialogFooter>
          <AlertDialogCancel disabled={remove.isPending}>
            {t('common:actions.cancel')}
          </AlertDialogCancel>
          <AlertDialogAction
            disabled={remove.isPending}
            onClick={(event) => {
              // Radix closes on click; stay open until the backend has answered.
              event.preventDefault();
              if (member) remove.mutate(member.id, { onSuccess: () => onOpenChange(false) });
            }}
          >
            {remove.isPending ? t('team:remove.submitting') : t('team:remove.submit')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
